import { Injectable } from '@angular/core';
import { TokenDto } from '@common/generated-api/models/token-dto';
import { UserDto } from '@common/generated-api/models/user-dto';
import { AdminUsersApiService } from '@common/generated-api/services/admin-users-api.service';
import { CommonAuthApiService } from '@common/generated-api/services/common-auth-api.service';
import { AppModes, AppService } from '@shared/services/app.service';
import { CompanyTypes } from '@shared/types/api-types';
import { RemoveNullableProps } from '@shared/utils/object.utils';
import { BehaviorSubject } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

export interface ICurrentCompanyType {
  client?: number;
  distributor?: number;
  producer?: number;
}

interface IFullUserData {
  user: UserDto & {userCompanyType?: CompanyTypes};
  token: TokenDto;
}

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  emptyFullUserData = { user: {}, token: null };
  fullUserData = new BehaviorSubject<IFullUserData>(this.emptyFullUserData);
  currentUser$ = this.fullUserData.asObservable().pipe(map((data) => data.user));
  /**
   * currentCompany$ value contains ONE of the ICurrentCompany properties with appropriate id,
   * it can be used in api methods where we have to pass either client, distributor or producer id
   */
  currentCompanyType$ = new BehaviorSubject<ICurrentCompanyType>({});
  currentCompanyID$ = new BehaviorSubject<number>(null);
  // currentCompany$ = new BehaviorSubject<ICurrentCompanyType>({});
  // triggerLoadCurrentUser$ = new Subject<{ userId: number }>();
  lsKeys = {
    authData: 'authData',
  };

  constructor(
    private authService: AuthService,
    private appService: AppService,
    private adminUsersApiService: AdminUsersApiService,
    private commonAuthApiService: CommonAuthApiService,
  ) {
    this.currentUser$.subscribe({
      next: (data) => {
        this.updateCurrentCompany(data);
      },
    });

    this.currentCompanyType$.subscribe({
      next: (data) => {

      }
    })
  }

  init() {
    return new Promise((resolve, reject) => {
      this.readSavedState();
      resolve(true);
    });
  }

  getCurrentUser() {
    return this.fullUserData.getValue()?.user;
  }

  loginUser(username: string, password: string) {
    let token: TokenDto;
    const stop = this.appService.startLoader();
    return this.commonAuthApiService
      .login({ body: { login: username, password } }, { disableErrorCatching: true })
      .pipe(
        switchMap((data) => {
          token = data.result;
          this.authService.authToken = data.result.authToken;
          return this.adminUsersApiService.getUser({ userId: data.result?.userId });
        }),
        map((data) => {
          const user = data.result;
          this.applyLoginResult(user, token);
          this.saveState();
        }),
        finalize(() => stop()),
        // catchError((err) => {
        //   console.log(err);
        //   ParseApiError(err);
        // })
      );
  }

  loginWithSavedState(data: IFullUserData) {
    this.applyLoginResult(data.user, data.token);
  }

  resetPassword(id: number) {
    return this.adminUsersApiService.updateUserPassword({
      userId: id,
      body: { password: null }
    }).pipe(map(data => data.result.password));
  }

  logoutUser() {
    this.fullUserData.next(this.emptyFullUserData);
    this.authService.setLoggedOut();
    this.saveState();
  }

  private applyLoginResult(user: UserDto, token: TokenDto) {
    this.fillFullUserData({ user, token })
    this.updateCurrentCompany(user);
    this.authService.authToken = token.authToken;
    this.authService.setLoggedIn({
      token: token?.authToken,
      isLoggedIn: !!token?.authToken,
      companyType: token?.companyType as AppModes,
      userId: token?.userId,
      user: user,
    });
  }

  private updateCurrentCompany(user: UserDto) {
    const companyProps: ICurrentCompanyType = {
      producer: user?.producer ?? null,
      distributor: user?.distributor ?? null,
      client: user?.client ?? null,
    };
    const companyProp = RemoveNullableProps(companyProps);
    if (Object.values(companyProp).filter((v) => v != null).length > 1) {
      // eslint-disable-next-line no-console
      console.error('User has few company types assigned!', user);
    }
    const id = companyProps.producer || companyProps.distributor || companyProps.client;
    this.currentCompanyID$.next(id);
    this.currentCompanyType$.next(companyProp);
  }

  private saveState() {
    this.setLocalStorageItem(this.lsKeys.authData, this.fullUserData.getValue());
  }

  private readSavedState() {
    const data = this.getLocalStorageItem(this.lsKeys.authData);
    // console.log('Read saved state', data);

    if (data?.user?.id) {
      if (data?.user?.picture) {
        delete data.user.picture;
      }
      this.loginWithSavedState(data);
      // reload user to update picture url
      this.reloadUser();
    }
  }

  private reloadUser() {
    this.adminUsersApiService.getUser({ userId: this.fullUserData.getValue().user?.id }).subscribe({
      next: (data) => {
        this.fillFullUserData({
          ...this.fullUserData.getValue(),
          user: data.result,
        });
      },
    });
  }

  private fillFullUserData(data: IFullUserData) {
    if (data?.user?.id && !data.user.userCompanyType) {
      let companyType: CompanyTypes;
      if (data.user.client) {
        companyType = CompanyTypes.CLIENT;
      } else if (data.user.distributor) {
        companyType = CompanyTypes.DISTRIBUTOR;
      } else if (data.user.producer) {
        companyType = CompanyTypes.PRODUCER;
      } else {
        console.error('Cannot determine user\'s company type', data.user);
      }
      data.user.userCompanyType = companyType;
    }
    this.fullUserData.next(data);
  }

  private setLocalStorageItem(key, value) {
    localStorage.setItem(key, JSON.stringify(value));
  }

  private getLocalStorageItem(key) {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }

  setModeForDev(mode: AppModes) {
    const data = this.fullUserData.getValue();
    if (data.user) {
      data.token.companyType = mode;
    }
    this.fillFullUserData(data)
    this.saveState();
  }
}
